import { MAX_BRANCHES, MAX_FLOW_STEPS, MAX_STEP_MEMBERS } from "../config";
import type {
  ApprovalApproverType,
  ApprovalBranch,
  ApprovalBranchDimension,
  ApprovalBranchDraft,
  ApprovalDepartmentOption,
  ApprovalDepartmentTreeNode,
  ApprovalDraftErrors,
  ApprovalFlowErrors,
  ApprovalFlowStep,
  ApprovalMemberCandidates,
  ApprovalMemberTreeNode,
  ApprovalSettingsDraft,
  ApprovalStepDraft,
  ApprovalTypeSettings,
} from "../types";

export {
  fetchApprovalMemberCandidates,
  fetchApprovalSettings,
  saveApprovalSettings,
  searchSettlementSuppliers,
} from "../repo/approvalSettingsRepo";

/** 分支范围维度的中文名，用在摘要、单选和提示里。 */
export const BRANCH_DIMENSION_LABELS: Record<ApprovalBranchDimension, string> = {
  settlement_supplier: "结算主体",
  department: "部门",
};

/** 主流程的流程序号；分支 1～3 的序号就是分支号。 */
export const MAIN_FLOW = 0;

/** 名字带停用标注：停用、已不是员工、已不能当结算主体或部门已不存在时带「（已停用）」。 */
export function formatMemberName(name: string, disabled: boolean): string {
  return disabled ? `${name}（已停用）` : name;
}

/** 流程名：主流程，或「分支 N」。 */
export function flowLabel(flow: number): string {
  return flow === MAIN_FLOW ? "主流程" : `分支 ${flow}`;
}

/** 句子里的流程名；分支序号后留一个空格，和后面的汉字隔开（与后端同一写法）。 */
export function flowLabelInText(flow: number): string {
  return flow === MAIN_FLOW ? flowLabel(flow) : `${flowLabel(flow)} `;
}

/** 一条流程最少几级：主流程能是 0 级（只配分支），分支至少 1 级、不用就清空整条分支。 */
export function minFlowSteps(flow: number): number {
  return flow === MAIN_FLOW ? 0 : 1;
}

/** 一条流程的摘要，照旧系统写法：逐级 `>>`、同级逗号，例如 `(张三)>>(李四,王五)`；没配时返回空串。 */
export function formatFlowSummary(steps: ApprovalFlowStep[] | null): string {
  if (!steps) return "";
  return steps
    .map((step) => (step.approverType === "dept_approver"
      ? "(所选部门的审批人)"
      : `(${step.members.map((member) => formatMemberName(member.name, member.disabled)).join(",")})`))
    .join(">>");
}

/** 一条分支的摘要，照旧系统写法：`分支1［指定结算主体：非遗资金池］-->(王五)>>(张三)`。 */
export function formatBranchSummary(branch: ApprovalBranch): string {
  const scope = branch.scopeDimension === "settlement_supplier" ? branch.suppliers : branch.departments;
  const names = scope.map((item) => formatMemberName(item.name, item.disabled)).join(",");
  return `分支${branch.branchNo}［指定${BRANCH_DIMENSION_LABELS[branch.scopeDimension]}：${names}］-->${formatFlowSummary(branch.steps)}`;
}

/** 一个空的「指定成员」级。 */
function emptyStep(): ApprovalStepDraft {
  return { approverType: "members", memberIds: [] };
}

/** 已存的各级转成编辑中的各级，只留成员 ID。 */
function toStepDrafts(steps: ApprovalFlowStep[]): ApprovalStepDraft[] {
  return steps.map((step) => ({ approverType: step.approverType, memberIds: step.members.map((member) => member.userId) }));
}

/** 从当前设置生成编辑稿；从没配过时主流程从一个空的第 1 级开始，只配了分支的保持 0 级。 */
export function createSettingsDraft(settings: ApprovalTypeSettings): ApprovalSettingsDraft {
  const neverConfigured = settings.version === 0 && !settings.mainFlow && !settings.branches.length;
  return {
    typeCode: settings.typeCode,
    typeLabel: settings.typeLabel,
    version: settings.version,
    branchDimensions: settings.branchDimensions,
    mainFlow: settings.mainFlow ? toStepDrafts(settings.mainFlow) : neverConfigured ? [emptyStep()] : [],
    branches: Array.from({ length: MAX_BRANCHES }, (_, index) => {
      const branch = settings.branches.find((item) => item.branchNo === index + 1);
      return branch
        ? {
            scopeDimension: branch.scopeDimension,
            supplierIds: branch.suppliers.map((item) => item.supplierId),
            departmentIds: branch.departments.map((item) => item.deptId),
            steps: toStepDrafts(branch.steps),
          }
        : null;
    }),
  };
}

/** 一条流程的各级；不用的分支返回空数组。 */
export function flowSteps(draft: ApprovalSettingsDraft, flow: number): ApprovalStepDraft[] {
  return flow === MAIN_FLOW ? draft.mainFlow : draft.branches[flow - 1]?.steps ?? [];
}

/** 改一条流程的各级；不用的分支原样返回。 */
function updateSteps(draft: ApprovalSettingsDraft, flow: number, change: (steps: ApprovalStepDraft[]) => ApprovalStepDraft[]): ApprovalSettingsDraft {
  if (flow === MAIN_FLOW) return { ...draft, mainFlow: change(draft.mainFlow) };
  return updateBranch(draft, flow, (branch) => ({ ...branch, steps: change(branch.steps) }));
}

/** 改一条在用的分支；不用的分支原样返回。 */
function updateBranch(draft: ApprovalSettingsDraft, branchNo: number, change: (branch: ApprovalBranchDraft) => ApprovalBranchDraft): ApprovalSettingsDraft {
  if (!draft.branches[branchNo - 1]) return draft;
  return { ...draft, branches: draft.branches.map((branch, index) => (branch && index === branchNo - 1 ? change(branch) : branch)) };
}

/** 末尾加一个空级；已满上限时原样返回。 */
export function addStep(draft: ApprovalSettingsDraft, flow: number): ApprovalSettingsDraft {
  return updateSteps(draft, flow, (steps) => (steps.length >= MAX_FLOW_STEPS ? steps : [...steps, emptyStep()]));
}

/** 删掉一级；不少于这条流程的最少级数。 */
export function removeStep(draft: ApprovalSettingsDraft, flow: number, index: number): ApprovalSettingsDraft {
  return updateSteps(draft, flow, (steps) => (steps.length <= minFlowSteps(flow) ? steps : steps.filter((_, current) => current !== index)));
}

/** 与相邻级互换；越界时原样返回。 */
export function moveStep(draft: ApprovalSettingsDraft, flow: number, index: number, offset: -1 | 1): ApprovalSettingsDraft {
  return updateSteps(draft, flow, (steps) => {
    const target = index + offset;
    if (target < 0 || target >= steps.length) return steps;
    const next = [...steps];
    [next[index], next[target]] = [next[target], next[index]];
    return next;
  });
}

/** 改一级的成员，去重并保持第一次出现的顺序。 */
export function setStepMembers(draft: ApprovalSettingsDraft, flow: number, index: number, memberIds: number[]): ApprovalSettingsDraft {
  return updateSteps(draft, flow, (steps) => steps.map((step, current) => (current === index ? { ...step, memberIds: [...new Set(memberIds)] } : step)));
}

/** 改一级的审批人类型，并清空成员：页面上看到什么就保存什么。 */
export function setStepApproverType(draft: ApprovalSettingsDraft, flow: number, index: number, approverType: ApprovalApproverType): ApprovalSettingsDraft {
  return updateSteps(draft, flow, (steps) => steps.map((step, current) => (current === index ? { approverType, memberIds: [] } : step)));
}

/** 使用一条分支：按这种审批支持的第一个维度、空范围、一个空级开始。 */
export function enableBranch(draft: ApprovalSettingsDraft, branchNo: number): ApprovalSettingsDraft {
  const branch: ApprovalBranchDraft = { scopeDimension: draft.branchDimensions[0], supplierIds: [], departmentIds: [], steps: [emptyStep()] };
  return { ...draft, branches: draft.branches.map((current, index) => (index === branchNo - 1 ? branch : current)) };
}

/** 清空一条分支：保存后这条分支不再使用。 */
export function clearBranch(draft: ApprovalSettingsDraft, branchNo: number): ApprovalSettingsDraft {
  return { ...draft, branches: draft.branches.map((current, index) => (index === branchNo - 1 ? null : current)) };
}

/** 改分支范围维度；两组范围都留着，切回来不丢。 */
export function setBranchDimension(draft: ApprovalSettingsDraft, branchNo: number, scopeDimension: ApprovalBranchDimension): ApprovalSettingsDraft {
  return updateBranch(draft, branchNo, (branch) => ({ ...branch, scopeDimension }));
}

/** 改分支的结算主体，去重。 */
export function setBranchSuppliers(draft: ApprovalSettingsDraft, branchNo: number, supplierIds: string[]): ApprovalSettingsDraft {
  return updateBranch(draft, branchNo, (branch) => ({ ...branch, supplierIds: [...new Set(supplierIds)] }));
}

/** 改分支的部门，去重；只算勾到的部门、不含下级。 */
export function setBranchDepartments(draft: ApprovalSettingsDraft, branchNo: number, departmentIds: number[]): ApprovalSettingsDraft {
  return updateBranch(draft, branchNo, (branch) => ({ ...branch, departmentIds: [...new Set(departmentIds)] }));
}

/** 校验编辑稿，规则与后端一致；提示写明是哪一条流程、第几级。 */
export function validateSettingsDraft(draft: ApprovalSettingsDraft): ApprovalDraftErrors {
  const flows: Record<number, ApprovalFlowErrors> = {};
  const messages: string[] = [];
  const add = (flow: number, change: (errors: ApprovalFlowErrors) => void, message: string) => {
    flows[flow] ??= { stepErrors: {} };
    change(flows[flow]);
    messages.push(message);
  };
  const checkSteps = (flow: number, steps: ApprovalStepDraft[]) => {
    const min = minFlowSteps(flow);
    if (steps.length < min) {
      const message = `${flowLabelInText(flow)}至少要有 ${min} 级`;
      add(flow, (errors) => { errors.flowError = message; }, message);
    }
    if (steps.length > MAX_FLOW_STEPS) {
      const message = `${flowLabelInText(flow)}最多 ${MAX_FLOW_STEPS} 级`;
      add(flow, (errors) => { errors.flowError = message; }, message);
    }
    steps.forEach((step, index) => {
      if (step.approverType !== "members") return;
      const short = !step.memberIds.length
        ? `第 ${index + 1} 级还没有选审批人`
        : step.memberIds.length > MAX_STEP_MEMBERS ? `第 ${index + 1} 级最多选 ${MAX_STEP_MEMBERS} 人，当前 ${step.memberIds.length} 人` : "";
      if (short) add(flow, (errors) => { errors.stepErrors[index] = short; }, `${flowLabelInText(flow)}的${short}`);
    });
  };
  if (!draft.mainFlow.length && draft.branches.every((branch) => !branch)) {
    const message = "至少要配主流程或一条分支";
    add(MAIN_FLOW, (errors) => { errors.flowError = message; }, message);
  }
  checkSteps(MAIN_FLOW, draft.mainFlow);
  draft.branches.forEach((branch, index) => {
    if (!branch) return;
    const branchNo = index + 1;
    const chosen = branch.scopeDimension === "settlement_supplier" ? branch.supplierIds : branch.departmentIds;
    if (!chosen.length) {
      const message = `${flowLabelInText(branchNo)}至少要选一个${BRANCH_DIMENSION_LABELS[branch.scopeDimension]}`;
      add(branchNo, (errors) => { errors.scopeError = message; }, message);
    }
    checkSteps(branchNo, branch.steps);
  });
  return { flows, messages };
}

/** 部门按上下级嵌套，员工挂主部门；找不到主部门的员工放进末尾的「未分配部门」。 */
export function buildMemberTree(candidates: ApprovalMemberCandidates): ApprovalMemberTreeNode[] {
  const departments = new Map<number, ApprovalMemberTreeNode>(
    candidates.departments.map((dept) => [dept.deptId, { key: `dept:${dept.deptId}`, title: dept.name, disabled: false, children: [] }]),
  );
  const roots: ApprovalMemberTreeNode[] = [];
  for (const dept of candidates.departments) {
    const node = departments.get(dept.deptId)!;
    const parent = dept.parentId === null ? undefined : departments.get(dept.parentId);
    (parent ? parent.children : roots).push(node);
  }
  const unassigned: ApprovalMemberTreeNode[] = [];
  for (const member of candidates.members) {
    const node = { key: `user:${member.userId}`, title: member.name, userId: member.userId, disabled: member.disabled, children: [] };
    const dept = member.mainDeptId === null ? undefined : departments.get(member.mainDeptId);
    (dept ? dept.children : unassigned).push(node);
  }
  if (unassigned.length) roots.push({ key: "dept:unassigned", title: "未分配部门", disabled: false, children: unassigned });
  return roots;
}

/** 选分支范围用的部门树：按上下级嵌套，上级找不到的当根。 */
export function buildDepartmentTree(departments: ApprovalDepartmentOption[]): ApprovalDepartmentTreeNode[] {
  const nodes = new Map<number, ApprovalDepartmentTreeNode>(
    departments.map((dept) => [dept.deptId, { key: String(dept.deptId), title: dept.name, children: [] }]),
  );
  const roots: ApprovalDepartmentTreeNode[] = [];
  for (const dept of departments) {
    const parent = dept.parentId === null ? undefined : nodes.get(dept.parentId);
    (parent ? parent.children : roots).push(nodes.get(dept.deptId)!);
  }
  return roots;
}

/** 按姓名或部门名搜索：保留命中的员工及其上级部门；部门名命中时带出整个部门。 */
export function filterMemberTree(tree: ApprovalMemberTreeNode[], keyword: string): ApprovalMemberTreeNode[] {
  const normalized = keyword.trim().toLocaleLowerCase();
  if (!normalized) return tree;
  return tree.flatMap((node) => {
    if (node.title.toLocaleLowerCase().includes(normalized)) return [node];
    const children = filterMemberTree(node.children, normalized);
    return children.length ? [{ ...node, children }] : [];
  });
}
